/**
 * ChartDataContext
 *
 * Provides global caching for chart data to prevent redundant API calls
 * when switching between dashboard tabs.
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { getBatchChartData } from '../services/dashboardService';
import apiClient from '../services/api';

const ChartDataContext = createContext(null);

export const useChartData = () => {
  const context = useContext(ChartDataContext);
  if (!context) {
    throw new Error('useChartData must be used within a ChartDataProvider');
  }
  return context;
};

// Max concurrent chart requests — matches the backend Snowflake connection pool size
const MAX_CONCURRENT = 5;

export const ChartDataProvider = ({ children }) => {
  // Cache: { chartId: { data, timestamp, loading, error } }
  const [cache, setCache] = useState({});

  // Ref mirror of cache — readable synchronously without being a useCallback dep
  const cacheRef = useRef({});

  // Synchronous in-flight set — prevents double-fire from StrictMode or rapid re-renders
  const inFlight = useRef(new Set());

  // Actual last updated time from database
  const [dataLastUpdated, setDataLastUpdated] = useState(null);

  // Cache TTL: 15 minutes
  const cacheTTL = 15 * 60 * 1000;

  // Keep cacheRef in sync whenever cache state changes
  useEffect(() => {
    cacheRef.current = cache;
  }, [cache]);

  /**
   * Check if any cached charts are stale
   */
  const hasStaleCache = useCallback(() => {
    const now = Date.now();
    return Object.values(cacheRef.current).some(
      (entry) => entry.timestamp && now - entry.timestamp > cacheTTL
    );
  }, [cacheTTL]);

  /**
   * Load data for multiple charts concurrently, rendering each as it arrives.
   * Uses a synchronous inFlight ref to prevent double-firing under React StrictMode.
   * Limits concurrency to MAX_CONCURRENT to avoid overwhelming the DB connection pool.
   */
  const loadBatchChartData = useCallback(async (chartIds, dashboardId = null) => {
    const now = Date.now();

    // Filter using cacheRef (synchronous) + inFlight ref (synchronous)
    // This prevents both StrictMode double-invoke races and reload loops
    const chartsToLoad = chartIds.filter((chartId) => {
      if (inFlight.current.has(chartId)) return false; // Already in-flight
      const cached = cacheRef.current[chartId];
      if (!cached) return true;
      if (cached.loading) return false;
      if (cached.error) return false; // Don't auto-retry errors
      if (now - cached.timestamp > cacheTTL) return true; // Stale
      return false;
    });

    if (chartsToLoad.length === 0) return;

    // Claim all charts synchronously before any awaits — prevents race conditions
    chartsToLoad.forEach((id) => inFlight.current.add(id));

    // Mark as loading in React state
    setCache((prev) => {
      const updated = { ...prev };
      chartsToLoad.forEach((chartId) => {
        updated[chartId] = { loading: true, data: null, error: null, timestamp: now };
      });
      return updated;
    });

    // Split chartsToLoad into batches of MAX_CONCURRENT so each HTTP request
    // sends multiple chart IDs — the backend's asyncio.gather() then queries
    // them in parallel, drastically reducing total round-trips and load time.
    const batches = [];
    for (let i = 0; i < chartsToLoad.length; i += MAX_CONCURRENT) {
      batches.push(chartsToLoad.slice(i, i + MAX_CONCURRENT));
    }

    // Run batches with MAX_CONCURRENT in-flight HTTP requests at a time
    let active = 0;
    const queue = [...batches];

    await new Promise((resolveAll) => {
      const runNext = () => {
        if (queue.length === 0 && active === 0) {
          resolveAll();
          return;
        }
        while (active < MAX_CONCURRENT && queue.length > 0) {
          const batch = queue.shift();
          active++;
          getBatchChartData(batch, dashboardId)
            .then((response) => {
              setCache((prev) => {
                const updated = { ...prev };
                Object.entries(response.data || {}).forEach(([id, data]) => {
                  updated[id] = { loading: false, data, error: null, timestamp: Date.now() };
                });
                Object.entries(response.errors || {}).forEach(([id, errorMsg]) => {
                  updated[id] = { loading: false, data: null, error: errorMsg, timestamp: Date.now() };
                });
                return updated;
              });
            })
            .catch((error) => {
              setCache((prev) => {
                const updated = { ...prev };
                batch.forEach((chartId) => {
                  updated[chartId] = {
                    loading: false,
                    data: null,
                    error: error.message || 'Failed to load chart data',
                    timestamp: Date.now(),
                  };
                });
                return updated;
              });
            })
            .finally(() => {
              batch.forEach((id) => inFlight.current.delete(id));
              active--;
              runNext();
            });
        }
      };
      runNext();
    });
  }, [cacheTTL]); // No `cache` dep — uses cacheRef instead, so reference stays stable

  /**
   * Get chart data from cache
   */
  const getChartData = useCallback(
    (chartId) => {
      return cache[chartId] || { loading: false, data: null, error: null, timestamp: 0 };
    },
    [cache] // keep cache dep here so chart components re-render when data arrives
  );

  /**
   * Clear the entire cache (useful for refresh)
   */
  const clearCache = useCallback(() => {
    setCache({});
  }, []);

  /**
   * Clear specific chart from cache
   */
  const clearChartCache = useCallback((chartId) => {
    setCache((prev) => {
      const updated = { ...prev };
      delete updated[chartId];
      return updated;
    });
  }, []);

  /**
   * Fetch the actual last updated time from database
   */
  const fetchDataLastUpdated = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/snowflake/last-updated');
      if (response.data.last_updated) {
        setDataLastUpdated(response.data.last_updated);
      }
    } catch (error) {
      console.error('Error fetching last updated timestamp:', error);
    }
  }, []);

  /**
   * Fetch last updated on mount
   */
  useEffect(() => {
    fetchDataLastUpdated();
  }, [fetchDataLastUpdated]);

  /**
   * Get the actual last updated timestamp from database
   * @returns {string|null} - ISO timestamp string or null if not loaded
   */
  const getLastUpdatedTime = useCallback(() => {
    return dataLastUpdated;
  }, [dataLastUpdated]);

  const value = {
    loadBatchChartData,
    getChartData,
    clearCache,
    clearChartCache,
    hasStaleCache,
    getLastUpdatedTime,
  };

  return <ChartDataContext.Provider value={value}>{children}</ChartDataContext.Provider>;
};

ChartDataProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ChartDataContext;
